import { Button, Link } from '@atoms'
import type { LinkProps } from '@atoms/Link'
import { PageLayout } from '@components/layouts/PageLayout'
import { PageHeaderNav, type PageTabs, type PageHeaderNavProps } from '@molecules/PageHeaderNav'
import { SpriteUrlProvider } from '@components/utils/spriteUrl'
import type { IconTypes } from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'
import type { GroupPageHeaderQueryType } from '@frontend/graphql/queries/groups'

import { addSupporterUrl } from '../../utils/routes'
import { displayAddress } from '../list-groups/GroupAddress'
import type { RouteProps } from '../../types/groupTypes'

const t = scopedTranslation('group_show')
const tBlasts = scopedTranslation('comms_management.blasts_index')
const tNav = scopedTranslation('shared.navigation')

export type GroupSection =
  | 'overview'
  | 'supporters'
  | 'events'
  | 'call_lists'
  | 'calling_sessions'
  | 'sms_inbox'
  | 'sms_blasts'
  | 'email_inbox'
  | 'email_blasts'
  | 'analytics'
  | 'shifts_overview'
  | 'imports'

export type GroupPageHeaderProps = {
  group: GroupPageHeaderQueryType['group']
  current: GroupSection
  permissions: {
    canAddSupporters?: boolean
    canCreateEvents?: boolean
    canCreateSmsBlasts?: boolean
    canCreateEmailBlasts?: boolean
    canImportSupporters?: boolean
  }
  routes: Pick<RouteProps['routes'], 'newEventRoute' | 'newSmsBlastRoute' | 'newEmailBlastRoute' | 'newImportRoute'>
  pageTabs?: PageTabs
  linkComponent?: PageHeaderNavProps['linkComponent']
  documentTitle?: boolean
  spriteUrl?: string
  onCreateCallList?: () => void
}

const HEADINGS: Partial<Record<GroupSection, 'inbox' | 'bulk_communications' | 'calls'>> = {
  call_lists: 'calls',
  calling_sessions: 'calls',
  sms_inbox: 'inbox',
  email_inbox: 'inbox',
  sms_blasts: 'bulk_communications',
  email_blasts: 'bulk_communications',
}

type CreateAction = {
  label: string
  href?: string
  onClick?: () => void
  allowed?: boolean
  as?: LinkProps['as']
  icon?: IconTypes
  dataCompId?: string
}

export function GroupPageHeader({
  group,
  current,
  permissions,
  routes,
  pageTabs,
  linkComponent,
  documentTitle = true,
  spriteUrl,
  onCreateCallList,
}: GroupPageHeaderProps) {
  const newEvent: CreateAction = {
    label: t('actions.new_event'),
    href: routes.newEventRoute,
    allowed: permissions.canCreateEvents,
  }
  const sections: Record<GroupSection, { icon: IconTypes; create?: CreateAction }> = {
    overview: { icon: 'users-01' },
    supporters: {
      icon: 'users-01',
      create: {
        label: tNav('add_new_supporter'),
        href: addSupporterUrl(group.id),
        allowed: permissions.canAddSupporters,
        as: 'modalLink',
        icon: 'user-plus-01',
        dataCompId: 'group-detail-page_add-new-supporter',
      },
    },
    events: { icon: 'calendar', create: newEvent },
    call_lists: {
      icon: 'phone-call-01',
      create: {
        label: t('actions.new_call_list'),
        onClick: onCreateCallList,
        allowed: !!onCreateCallList,
      },
    },
    calling_sessions: { icon: 'phone-call-01' },
    sms_inbox: { icon: 'message-chat-square' },
    sms_blasts: {
      icon: 'message-chat-square',
      create: {
        label: tBlasts('new_blast.sms'),
        href: routes.newSmsBlastRoute,
        allowed: permissions.canCreateSmsBlasts,
      },
    },
    email_inbox: { icon: 'mail-01' },
    email_blasts: {
      icon: 'mail-01',
      create: {
        label: tBlasts('new_blast.email'),
        href: routes.newEmailBlastRoute,
        allowed: permissions.canCreateEmailBlasts,
      },
    },
    analytics: { icon: 'bar-chart-square-02' },
    shifts_overview: { icon: 'calendar-check-01', create: newEvent },
    imports: {
      icon: 'upload-cloud-02',
      create: {
        label: t('actions.import_supporters'),
        href: routes.newImportRoute,
        allowed: permissions.canImportSupporters,
        as: 'inertiaLink',
        icon: 'upload-cloud-02',
      },
    },
  }
  const { icon, create } = sections[current]
  const isOverview = current === 'overview'
  const grouped = HEADINGS[current]
  const subtitle = [
    isOverview ? null : group.name,
    displayAddress(group),
    t('header.supporters_count', { count: group.supporters.totalCount }),
  ]

  return (
    <SpriteUrlProvider url={spriteUrl}>
      <PageLayout.Header
        icon={icon}
        heading={isOverview ? group.name : t(`sections.${grouped ?? current}`)}
        subtitle={subtitle.filter(Boolean).join(' · ')}
        nav={pageTabs && <PageHeaderNav ariaLabel={pageTabs.label} items={pageTabs.items} linkComponent={linkComponent} />}
        actions={
          create?.allowed &&
          (create.onClick ? (
            <Button leadingIcon={create.icon ?? 'plus'} onClick={create.onClick}>
              {create.label}
            </Button>
          ) : (
            create.href && (
              <Link
                as={create.as ?? 'a'}
                leadingIcon={create.icon ?? 'plus'}
                href={create.href}
                dataCompId={create.dataCompId}
              >
                {create.label}
              </Link>
            )
          ))
        }
        documentTitle={documentTitle}
      />
    </SpriteUrlProvider>
  )
}
